import React, { useState } from "react";
import { Card, Button, message, Typography, Flex, Result, Image, Alert } from "antd";
import { DONOR_SERVICE } from "../../shared/services/donor-service";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FrownOutlined } from '@ant-design/icons';
import logo from '../../assets/images/hff_donors_logo_dark.png'; // Adjust path as needed
import "./Unsubscribe.scss";

const { Title, Text, Paragraph } = Typography;

interface UnsubscribePayload {
  email: string;
  token: string;
  tenant_id: string;
}

const UnsubscribeComponent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get all required parameters from URL
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const tenant_id = searchParams.get('tenant_id');

  const handleUnsubscribe = async () => {
    setError(null); // Reset error state before new attempt
    
    if (!email || !token || !tenant_id) {
      setError("Invalid unsubscribe link");
      return;
    }

    const payload: UnsubscribePayload = {
      email,
      token,
      tenant_id
    };

    try {
      setLoading(true);
      const response = await DONOR_SERVICE.unsubscribe("/api/crm/unsubscribe", payload) as any;
      
      if (response.success) {
        setIsUnsubscribed(true);
      } else {
        setError(response.message || "Failed to unsubscribe");
      }
    } catch (error: any) {
      console.error("Unsubscribe error:", error);
      setError(error?.response?.data?.message || "An error occurred while processing your request");
      
      if (error?.response?.status === 401) {
        setError("Invalid or expired unsubscribe link");
      }
    } finally {
      setLoading(false);
    }




  };

  return (
    <Flex vertical align="center" justify="center" className="unsubscribe-container">
      <Card className="unsubscribe-card">
        <div className="logo-container">
          <Image
            src={logo}
            alt="Company Logo"
            preview={false}
            height={60}
          />
        </div>

        {isUnsubscribed && (
          <Alert
            message="Successfully Unsubscribed"
            description={
              <Text>
                You have been successfully unsubscribed from our mailing list.
              </Text>
            }
            type="success"
            className="unsubscribe-alert"
          />
        )}

        {error && (
          <Alert
            message="Unsubscribe Failed"
            description={error}
            type="error"
            className="unsubscribe-alert"
          />
        )}

        {!isUnsubscribed ? (
          <Result
            icon={<FrownOutlined className="sad-icon" />}
            title={<Title level={2}>We're Sad to See You Go</Title>}
            subTitle={
              <Flex vertical gap={16} className="unsubscribe-content">
                <Paragraph>
                  We'll miss sending you updates and keeping you informed about our latest news and activities.
                </Paragraph>
                <Paragraph type="secondary">
                  Are you sure you want to unsubscribe from our mailing list?
                </Paragraph>
              </Flex>
            }
            extra={[
              <Button 
                key="cancel" 
                className="keep-subscribed-btn mt-6 input-height"
                onClick={() => navigate('/')}
                type="primary"
              >
                Keep Me Subscribed
              </Button>,
              <Button
                key="unsubscribe"
                type="default"
                danger
                loading={loading}
                onClick={handleUnsubscribe}
                className="unsubscribe-btn btn-danger mt-6 input-height"
              >
                Yes, Unsubscribe Me
              </Button>
            ]}
          />
        ) : (
          <Button 
            type="primary"
            onClick={() => navigate('/')}
            className="close-btn mt-6"
          >
            Return to Home
          </Button>
        )}
      </Card>
    </Flex>
  );
};

export default UnsubscribeComponent;
